// ReconWorkspace — one shared, config-driven reconciliation UI for every PSP (Stripe / Authorize.Net /
// PaySimple). The component owns all layout, state, filtering, paging, account tabs, and the shared
// OrderPeekDialog; each PSP supplies a config (src/config/recon/*.js, docs/recon-workspace-design.md)
// that declares its endpoints, columns, buckets, stat tiles, filters, and PSP-specific dialogs.
// Existing per-PSP pages are untouched — this renders on new "(New)" routes alongside them.
//
// config contract (abbreviated):
//   title, SectionNav?
//   filters: { period, account, search }
//   accountAllTab?, defaultAccount?                account tabs (Stripe-style); 'All accounts' tab + default
//   extraFilters?: [{ key, default, minWidth?, options:[{value,label}], match(r,val) }]  e.g. Stripe origin
//   list({year,month,account}) -> data
//   run?({account}) -> Promise<string|void>, runTooltip?   returns a toast message if any
//   rowKey(r), bucketOf(status)
//   buckets: [{key,label}], tabNote?(tab, extra)
//   statTiles(data, ctx) -> [{label,value,sub,color,tabKey}]
//   columns: [{ key, header, width?, align?, render(r, ctx) }]
//   searchText?(r, ctx) -> string
//   loadMaps?() -> object, mapKey?(r)
//   link?({row, orderId}) -> Promise
//   orderPeek: { link(peekCtx), idMetaKeys, idLabel }
//   renderDialogs?(ctx) -> JSX
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, CircularProgress, Alert, Snackbar, Pagination, TextField, Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../common/PageLayout';
import OrderPeekDialog from './OrderPeekDialog';
import WorkspaceHeader from './WorkspaceHeader';
import { fmtMoney, fmtDate, fmtDateTime, YEAR_OPTIONS, MONTH_OPTIONS } from './reconShared';

export default function ReconWorkspace({ config }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const PER = config.perPage || 25;

  const [year, setYear] = useState(() => searchParams.get('year') || 'all');
  const [month, setMonth] = useState(() => searchParams.get('month') || 'all');
  const [account, setAccount] = useState(() => searchParams.get('acct') || searchParams.get('account') || config.defaultAccount || 'all');
  const [tab, setTab] = useState(() => (config.buckets.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'all'));
  const [search, setSearch] = useState('');
  const [extra, setExtra] = useState(() => Object.fromEntries((config.extraFilters || []).map((f) => [f.key, f.default])));
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');

  const [orderDlg, setOrderDlg] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [createRow, setCreateRow] = useState(null);
  const [mapSub, setMapSub] = useState(null);
  const [maps, setMaps] = useState({});

  const selectAccount = useCallback((a) => {
    setAccount(a);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('acct', a); return p; }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await config.list({ year, month, account })); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load reconciliation'); }
    finally { setLoading(false); }
  }, [config, year, month, account]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab, account, year, month, search, JSON.stringify(extra)]);

  const loadMaps = useCallback(async () => {
    if (!config.loadMaps) return;
    try { setMaps(await config.loadMaps()); } catch { /* mappings optional */ }
  }, [config]);
  useEffect(() => { loadMaps(); }, [loadMaps]);
  const mapFor = (r) => (config.mapKey ? maps[config.mapKey(r)] || null : null);
  const allocSummary = (m) => (Array.isArray(m?.splits) ? m.splits.map((s) => `${s.label} ${fmtMoney(s.amount)}`).join(' · ') : '');

  // If the selected account isn't reachable once data loads, fall back to the first one.
  useEffect(() => {
    if (!(config.filters?.account && Array.isArray(data?.accounts) && data.accounts.length)) return;
    const valid = (config.accountAllTab ? ['all'] : []).concat(data.accounts.map((a) => a.key));
    if (!valid.includes(account)) selectAccount(valid[0]);
  }, [data, account, config, selectAccount]);

  const runNow = async () => {
    if (!config.run) return;
    setRunning(true); setErr('');
    try { const msg = await config.run({ account }); setToast(msg || 'Reconciliation run complete'); await load(); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Run failed'); }
    finally { setRunning(false); }
  };

  const linkRow = async (row) => {
    try {
      await config.link({ row, orderId: row.matched_order_id });
      setToast(`Linked to ${row.matched_order_number}`);
      await load();
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Link failed'); }
  };

  const ctx = {
    data, account, tab, extra, setTab, reload: load, setToast, navigate,
    mapFor, allocSummary,
    openOrderPeek: setOrderDlg, openDetail: setDetailRow, openCreate: setCreateRow, openMap: setMapSub,
    linkRow, reloadMaps: loadMaps,
    slots: { detailRow, createRow, mapSub, orderDlg },
    closeDetail: () => setDetailRow(null), closeCreate: () => setCreateRow(null), closeMap: () => setMapSub(null),
    fmtMoney, fmtDate, fmtDateTime,
  };

  const rows = data?.rows || [];
  let filtered = (config.filters?.account && account !== 'all') ? rows.filter((r) => r.account === account) : rows;
  (config.extraFilters || []).forEach((f) => { const v = extra[f.key]; if (v && v !== 'all') filtered = filtered.filter((r) => f.match(r, v)); });
  const rq = search.trim().toLowerCase();
  const searched = (config.searchText && rq) ? filtered.filter((r) => config.searchText(r, ctx).toLowerCase().includes(rq)) : filtered;
  const shown = searched.filter((r) => tab === 'all' || config.bucketOf(r.status) === tab);
  const tabCount = (key) => (key === 'all' ? searched.length : searched.filter((r) => config.bucketOf(r.status) === key).length);
  const pageCount = Math.max(1, Math.ceil(shown.length / PER));
  const pageSafe = Math.min(page, pageCount);
  const paged = shown.slice((pageSafe - 1) * PER, (pageSafe - 1) * PER + PER);

  const lastRun = data?.last_run ? new Date(data.last_run) : null;
  const lastRunLabel = lastRun
    ? lastRun.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT'
    : 'never';

  const pager = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{shown.length} records · {PER} per page</Typography>
      <Pagination count={pageCount} page={pageSafe} onChange={(e, p) => setPage(p)} size="small" color="primary" siblingCount={0} boundaryCount={1} />
    </Box>
  );

  // Active tile (its tab is selected) highlights with its OWN related color — same treatment as the
  // Subscriptions workspace StatTile: colored border + tinted fill + "Showing" chip.
  const statCard = ({ label, value, sub, color, tabKey }) => {
    const key = color || 'primary';
    const active = !!(tabKey && tab === tabKey);
    return (
      <Paper key={label} variant="outlined" onClick={tabKey ? () => setTab(tabKey) : undefined}
        sx={(theme) => ({
          p: 2, height: '100%', position: 'relative', borderRadius: 2,
          border: active ? 2 : 1, borderColor: active ? `${key}.main` : 'divider',
          borderTop: 3, borderTopColor: color ? `${color}.main` : 'divider',
          bgcolor: active ? alpha(theme.palette[key]?.main || theme.palette.primary.main, 0.12) : 'background.paper',
          boxShadow: active ? 3 : 0, cursor: tabKey ? 'pointer' : 'default', transition: '.12s',
          '&:hover': tabKey ? { boxShadow: 3, borderColor: `${key}.main` } : {},
        })}>
        {active && <Chip size="small" color={color || 'primary'} label="Showing" sx={{ position: 'absolute', top: 8, right: 8, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }} />}
        <Typography variant="caption" sx={{ color: color ? `${color}.dark` : 'text.secondary', fontWeight: active ? 700 : 600 }}>{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: color ? `${color}.dark` : 'text.primary' }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>
      </Paper>
    );
  };

  const tiles = config.statTiles ? config.statTiles(data, ctx) : [];
  const note = config.tabNote ? config.tabNote(tab, extra) : null;

  // Filters describe the view (search + period/origin). The Sync action lives in the header, not here.
  const filterBar = (config.filters?.search || (config.extraFilters || []).length > 0 || config.filters?.period) ? (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {config.filters?.search && (
        <TextField size="small" placeholder="Search name, id, order…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
      )}
      {(config.extraFilters || []).map((f) => (
        <FormControl key={f.key} size="small">
          <Select value={extra[f.key]} onChange={(e) => setExtra((x) => ({ ...x, [f.key]: e.target.value }))} sx={{ minWidth: f.minWidth || 150 }}>
            {f.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      ))}
      {config.filters?.period && (
        <>
          <FormControl size="small"><Select value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 120 }}>{YEAR_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</Select></FormControl>
          <FormControl size="small"><Select value={month} onChange={(e) => setMonth(e.target.value)} sx={{ minWidth: 140 }}>{MONTH_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</Select></FormControl>
        </>
      )}
    </Box>
  ) : null;

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box>
        <WorkspaceHeader
          overline={config.overline || 'Payments'}
          provider={config.provider}
          title={config.title}
          account={account}
          accounts={data?.accounts || config.fallbackAccounts || []}
          onAccount={selectAccount}
          accountAllOption={!!config.accountAllTab}
          onSync={config.run ? runNow : undefined}
          syncing={running}
          syncTitle={config.runTooltip}
          lastSynced={lastRunLabel}
          sectionNav={config.sectionNav ? {
            active: config.sectionNav.active,
            items: config.sectionNav.items,
            onNav: (key) => { const it = config.sectionNav.items.find((x) => x.key === key); if (it) navigate(`${it.path}${account && account !== 'all' ? `?acct=${account}` : ''}`); },
          } : undefined}
          filterBar={filterBar}
        />

        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

        {tiles.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {tiles.map((t) => <Grid key={t.label} item xs={6} md={12 / Math.min(tiles.length, 4)}>{statCard(t)}</Grid>)}
          </Grid>
        )}

        <Paper variant="outlined">
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {config.buckets.map((t) => (<Tab key={t.key} value={t.key} label={`${t.label} (${tabCount(t.key)})`} />))}
          </Tabs>

          {note && (
            <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{note}</Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : shown.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              {tab === 'no_order' ? '✓ No missing-order records in this period.' : 'Nothing here for this period.'}
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              {pager}
              <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', minWidth: 920, '& td, & th': { px: 1, verticalAlign: 'top', wordBreak: 'break-word' } }}>
                <TableHead>
                  <TableRow>
                    {config.columns.map((c) => (<TableCell key={c.key} align={c.align || 'left'} sx={c.width ? { width: c.width } : undefined}>{c.header}</TableCell>))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((r) => (
                    <TableRow key={config.rowKey(r)} hover>
                      {config.columns.map((c) => (<TableCell key={c.key} align={c.align || 'left'}>{c.render(r, ctx)}</TableCell>))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {pager}
            </TableContainer>
          )}
        </Paper>
      </Box>

      <OrderPeekDialog
        ctx={orderDlg}
        peek={config.orderPeek}
        onClose={() => setOrderDlg(null)}
        onLinked={(c) => { setOrderDlg(null); setToast(`Linked to ${c.orderNumber}`); load(); }}
        navigate={navigate}
      />
      {config.renderDialogs ? config.renderDialogs(ctx) : null}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageLayout>
  );
}
